<script setup lang="ts">
  import { defineProps, defineEmits } from "vue";
  import { Text } from "../Text";
  import { CheckboxProps, CheckboxEmits } from "./types";

  const { modelValue, label, disabled, description } = defineProps<CheckboxProps>();
  const emit = defineEmits<CheckboxEmits>();

  const updateValue = (event: Event) => {
    emit("update:modelValue", (event.target as HTMLInputElement).checked);
  };
</script>

<template>
  <div class="checkbox">
    <label class="checkbox__label">
      <div class="checkbox__fake-input" :disabled="disabled ? disabled : undefined">
        <svg v-if="modelValue" width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.70711 0.292893C10.0976 0.683417 10.0976 1.31658 9.70711 1.70711L4.70711 6.70711C4.31658 7.09763 3.68342 7.09763 3.29289 6.70711L0.292893 3.70711C-0.0976311 3.31658 -0.0976311 2.68342 0.292893 2.29289C0.683417 1.90237 1.31658 1.90237 1.70711 2.29289L4 4.58579L8.29289 0.292893C8.68342 -0.0976311 9.31658 -0.0976311 9.70711 0.292893Z"
            style="fill: var(--sf-base-icon-white)"
          />
        </svg>
      </div>
      <input
        type="checkbox"
        :checked="modelValue"
        class="checkbox__input"
        :disabled="disabled ? disabled : undefined"
        @change="updateValue"
      />
      <Text v-if="label" class="checkbox__text">{{ label }}</Text>
    </label>
    <Text v-if="description" class="checkbox__description" font="body-regular-14" color="secondary">{{ description }}</Text>
  </div>
</template>

<style scoped>
  .checkbox {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .checkbox__label {
    display: flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
  }

  .checkbox__fake-input {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid var(--sf-base-border-dark);
    border-radius: 4px;
    background-color: var(--sf-control-background-default);
  }

  .checkbox__input {
    display: none;
  }

  .checkbox__description {
    display: inline-flex;
    padding-left: 28px;
  }

  .checkbox__label:hover .checkbox__fake-input {
    background-color: var(--sf-control-background-hover);
    border-color: var(--sf-base-border-black);
  }

  .checkbox:has(input:checked) .checkbox__fake-input {
    border-color: transparent;
    background-color: var(--sf-control-background-active-default);
  }

  .checkbox:has(input:checked) .checkbox__label:hover .checkbox__fake-input {
    background-color: var(--sf-control-background-active-hover);
  }

  .checkbox:has(input:disabled) .checkbox__label .checkbox__fake-input {
    background-color: var(--sf-base-background-secondary);
    border-color: var(--sf-base-border-primary);
  }

  .checkbox:has(input:checked:disabled) .checkbox__label .checkbox__fake-input {
    background-color: var(--sf-control-background-active-disabled);
  }
</style>
